import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { inputStyle, labelStyle } from '../contracts/contractsApi';
import { REGISTRIES, banner } from './packsApi';

const FIELDS = [
  ['legalName', 'Legal name', 'Agriware Ltd'], ['companyNumber', 'Company number'], ['registry', 'Registry'],
  ['registeredAddress', 'Registered address', '', true], ['signatoryName', 'Default signatory name'], ['signatoryRole', 'Default signatory role'],
];

// Agriware's own details, merged into every template. Copied into each pack when
// it is sent, so later edits never change a sent or signed document.
export default function CompanyDetails() {
  const [v, setV] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'settings', 'company')).then(s => {
      const d = s.exists() ? s.data() : {};
      setV({ registry: REGISTRIES[0], ...d });
      if (d.logoPath) getDownloadURL(storageRef(storage, d.logoPath)).then(setLogoUrl).catch(() => {});
    }).catch(e => setMsg({ tone: 'danger', text: e.message }));
  }, []);

  const saveAll = async () => {
    setBusy(true); setMsg(null);
    try {
      const data = Object.fromEntries(FIELDS.map(([k]) => [k, String(v[k] || '').trim()]));
      await setDoc(doc(db, 'settings', 'company'), { ...data, logoPath: v.logoPath || null, updatedAt: serverTimestamp() }, { merge: true });
      setMsg({ tone: 'success', text: 'Saved. Packs sent from now on use these details.' });
    } catch (e) { setMsg({ tone: 'danger', text: e.message }); }
    setBusy(false);
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      const path = `settings/logo/${Date.now()}-${file.name.replace(/[^\w.-]+/g, '_')}`;
      await uploadBytes(storageRef(storage, path), file, { contentType: file.type });
      setV(x => ({ ...x, logoPath: path }));
      setLogoUrl(await getDownloadURL(storageRef(storage, path)));
      setMsg({ tone: 'warning', text: 'Logo uploaded. Press Save to use it.' });
    } catch (e) { setMsg({ tone: 'danger', text: e.message }); }
    setBusy(false);
  };

  return (
    <Layout>
      <PageHeader title="Company details" subtitle="Agriware's details, filled into every contract template" />
      <div style={{ padding: 28, maxWidth: 760 }}>
        {msg && <div style={{ ...banner(msg.tone), marginBottom: 12 }}>{msg.text}</div>}
        {v && (
          <Card>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {FIELDS.map(([k, label, ph, multi]) => (
                <div key={k} style={{ gridColumn: multi ? '1 / -1' : undefined }}>
                  <label style={labelStyle}>{label} <span style={{ fontFamily: 'var(--font-mono)' }}>{`{{agriware.${k}}}`}</span></label>
                  {k === 'registry'
                    ? <select style={inputStyle} value={v.registry} onChange={e => setV({ ...v, registry: e.target.value })}>{REGISTRIES.map(r => <option key={r}>{r}</option>)}</select>
                    : multi
                      ? <textarea rows={3} style={{ ...inputStyle, height: 'auto', padding: 8 }} value={v[k] || ''} onChange={e => setV({ ...v, [k]: e.target.value })} />
                      : <input style={inputStyle} placeholder={ph} value={v[k] || ''} onChange={e => setV({ ...v, [k]: e.target.value })} />}
                </div>
              ))}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Logo for template headers (optional)</label>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {logoUrl && <img src={logoUrl} alt="" style={{ height: 36 }} />}
                  <input type="file" accept="image/*" onChange={e => uploadLogo(e.target.files[0])} style={{ fontSize: 12 }} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}><Button loading={busy} onClick={saveAll}>Save</Button></div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
